import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Smartphone, Clock, CheckCircle2, AlertCircle, MapPin, Phone, Mail,
  ArrowLeft, Loader2, ScanLine, PackageCheck, XCircle, Hourglass,
  Gift, Tag, ShoppingBag,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_ICONS = {
  termine:    { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40' },
  a_recuperer:{ icon: PackageCheck, color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40' },
  en_cours:   { icon: Clock,        color: 'text-amber-400',   bg: 'bg-amber-400/10',   border: 'border-amber-400/40' },
  diagnostic: { icon: ScanLine,     color: 'text-cyan-400',    bg: 'bg-cyan-400/10',    border: 'border-cyan-400/40' },
  attente:    { icon: Hourglass,    color: 'text-slate-300',   bg: 'bg-slate-500/10',   border: 'border-slate-500/40' },
  annule:     { icon: XCircle,      color: 'text-rose-400',    bg: 'bg-rose-400/10',    border: 'border-rose-400/40' },
  inconnu:    { icon: AlertCircle,  color: 'text-slate-400',   bg: 'bg-slate-500/10',   border: 'border-slate-500/40' },
};

const RepairStatusPage = () => {
  const { ref } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/api/public/suivi/${encodeURIComponent(ref)}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch((e) => { if (!cancel) setError(e?.response?.status === 404 ? 'Reparation introuvable' : 'Erreur de chargement'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [ref]);

  const statusConfig = STATUS_ICONS[data?.status_key] || STATUS_ICONS.inconnu;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white">
      {/* Top bar */}
      <header className="border-b border-slate-800 bg-black/40 backdrop-blur sticky top-0 z-10" data-testid="public-suivi-header">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 font-bold tracking-wider">
            <ArrowLeft className="w-4 h-4" />
            <span>R.E.MOBILES</span>
          </Link>
          <span className="text-xs text-slate-400">Suivi de reparation</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8" data-testid="public-suivi-page">
        {loading && (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin mr-3" /> Chargement...
          </div>
        )}

        {!loading && error && (
          <div className="bg-rose-500/10 border border-rose-500/40 rounded-2xl p-8 text-center" data-testid="public-suivi-error">
            <XCircle className="w-12 h-12 text-rose-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-rose-200 mb-2">{error}</h2>
            <p className="text-slate-400 text-sm">La reference <span className="font-mono text-amber-300">{ref}</span> ne correspond a aucune reparation enregistree.</p>
            <Link to="/" className="inline-block mt-6 px-5 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium">Retour a l'accueil</Link>
          </div>
        )}

        {!loading && !error && data && (
          <div className="space-y-6">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Reference</p>
                <h1 className="text-3xl font-black text-amber-400 font-mono" data-testid="public-suivi-ref">{data.ref}</h1>
              </div>
              {data.client_initials && (
                <p className="text-sm text-slate-400">Client : <span className="text-slate-200">{data.client_initials}</span></p>
              )}
            </div>

            {/* Status card */}
            <div className={`rounded-2xl border ${statusConfig.border} ${statusConfig.bg} p-6 flex items-center gap-4`} data-testid="public-suivi-status">
              <StatusIcon className={`w-12 h-12 ${statusConfig.color} flex-shrink-0`} />
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400">Statut</p>
                <p className={`text-xl font-bold ${statusConfig.color}`}>{data.status_label}</p>
                {data.status_key === 'a_recuperer' || data.status_key === 'termine' ? (
                  <p className="text-sm text-slate-200 mt-1">Votre appareil est pret. Venez le recuperer en boutique.</p>
                ) : null}
              </div>
            </div>

            {/* Device info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.model && (
                <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 flex items-start gap-3">
                  <Smartphone className="w-5 h-5 text-cyan-400 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Appareil</p>
                    <p className="text-slate-100 font-medium">{data.model}</p>
                  </div>
                </div>
              )}
              {data.received_at && (
                <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 flex items-start gap-3">
                  <Clock className="w-5 h-5 text-cyan-400 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Depot</p>
                    <p className="text-slate-100 font-medium">{data.received_at}</p>
                  </div>
                </div>
              )}
              {data.expected_at && (
                <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 flex items-start gap-3">
                  <Hourglass className="w-5 h-5 text-cyan-400 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Retour prevu</p>
                    <p className="text-slate-100 font-medium">{data.expected_at}</p>
                  </div>
                </div>
              )}
              {data.completed_at && (
                <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Termine le</p>
                    <p className="text-slate-100 font-medium">{data.completed_at}</p>
                  </div>
                </div>
              )}
              {data.price && (
                <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 flex items-start gap-3 sm:col-span-2">
                  <span className="w-5 h-5 mt-0.5 text-amber-400 font-bold">€</span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Prix</p>
                    <p className="text-slate-100 font-bold text-lg">{data.price}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Boutique contact */}
            {data.boutique && (data.boutique.phone || data.boutique.email || data.boutique.address) && (
              <div className="bg-gradient-to-r from-amber-500/10 to-rose-500/10 border border-amber-500/30 rounded-2xl p-5" data-testid="public-suivi-contact">
                <h2 className="text-base font-semibold text-amber-300 mb-3">Contacter la boutique</h2>
                <div className="space-y-2 text-sm">
                  {data.boutique.address && (
                    <div className="flex items-start gap-2 text-slate-200">
                      <MapPin className="w-4 h-4 mt-0.5 text-amber-300" />
                      <span>{data.boutique.address}</span>
                    </div>
                  )}
                  {data.boutique.phone && (
                    <a href={`tel:${data.boutique.phone}`} className="flex items-center gap-2 text-slate-200 hover:text-amber-300">
                      <Phone className="w-4 h-4 text-amber-300" />
                      <span>{data.boutique.phone}</span>
                    </a>
                  )}
                  {data.boutique.email && (
                    <a href={`mailto:${data.boutique.email}?subject=Suivi reparation ${data.ref}`} className="flex items-center gap-2 text-slate-200 hover:text-amber-300">
                      <Mail className="w-4 h-4 text-amber-300" />
                      <span>{data.boutique.email}</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Phase 2.6 — Pickup promo banner (only when status_key in {termine, a_recuperer, livre}) */}
            {data.pickup_promo && (
              <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/15 via-rose-500/10 to-amber-500/5 p-5 sm:p-6 shadow-lg shadow-amber-500/10" data-testid="public-suivi-promo">
                <div className="flex items-start gap-4">
                  <div className="hidden sm:flex w-12 h-12 rounded-full bg-amber-500/20 border border-amber-400/40 items-center justify-center flex-shrink-0">
                    <Gift className="w-6 h-6 text-amber-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="inline-flex items-center gap-1 text-[10px] sm:text-xs uppercase tracking-widest text-amber-300/90 mb-1 font-semibold">
                      <Tag className="w-3 h-3" /> Offre boutique
                    </p>
                    <h3 className="text-lg sm:text-xl font-black text-amber-300 leading-tight" data-testid="public-suivi-promo-title">
                      {data.pickup_promo.title}
                    </h3>
                    {data.pickup_promo.description && (
                      <p className="text-sm text-slate-200 mt-2 leading-relaxed">
                        {data.pickup_promo.description}
                      </p>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      {data.pickup_promo.code && (
                        <div className="inline-flex items-center gap-2 bg-black/40 border border-amber-400/40 rounded-lg px-3 py-1.5">
                          <span className="text-xs uppercase tracking-wider text-slate-400">Code</span>
                          <span className="font-mono font-bold text-amber-300 text-sm tracking-wider" data-testid="public-suivi-promo-code">
                            {data.pickup_promo.code}
                          </span>
                        </div>
                      )}
                      {data.pickup_promo.cta_url && (
                        <Link
                          to={data.pickup_promo.cta_url}
                          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm transition-colors"
                          data-testid="public-suivi-promo-cta"
                        >
                          <ShoppingBag className="w-4 h-4" />
                          {data.pickup_promo.cta_label || 'Voir nos accessoires'}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="text-center text-xs text-slate-500 py-6">
        © R.E.MOBILES — Vos donnees personnelles ne sont pas affichees publiquement.
      </footer>
    </div>
  );
};

export default RepairStatusPage;
